import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCampaigns } from '../hooks/useCampaigns';
import { useLanguage } from '../contexts/LanguageContext';
import { getCampaignLang } from '../translations/campaign_lang';
import { Campaign, CampaignStatus } from '../types/campaign';
import CommonPageHeader from '../components/common/CommonPageHeader';
import LoadingScreen from '../components/common/LoadingScreen';
import ConfirmDeleteModal from '../components/common/ConfirmDeleteModal';
import {
  EnvelopeIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

const statusColors: Record<CampaignStatus, string> = {
  draft: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  scheduled: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  sending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  sent: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  paused: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const Campaigns: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const tr = getCampaignLang(language);
  const { campaigns, loading, deleteCampaign, duplicateCampaign, updateCampaignStatus } = useCampaigns();
  const [campaignToDelete, setCampaignToDelete] = useState<Campaign | null>(null);
  const [campaignToSend, setCampaignToSend] = useState<Campaign | null>(null);

  const getStatusLabel = (status: CampaignStatus) => {
    const map: Record<CampaignStatus, string> = {
      draft: tr.campaign.statusDraft,
      scheduled: tr.campaign.statusScheduled,
      sending: tr.campaign.statusSending,
      sent: tr.campaign.statusSent,
      paused: tr.campaign.statusPaused,
      cancelled: tr.campaign.statusCancelled,
    };
    return map[status] || status;
  };

  const getTypeLabel = (type: string) => {
    return type === 'email' ? tr.campaign.typeEmail : tr.campaign.typeSms;
  };

  const formatDate = (date?: Date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString(language === 'en' ? 'en-US' : 'hu-HU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleSend = async () => {
    if (!campaignToSend) return;
    try {
      await updateCampaignStatus(campaignToSend.id, 'sending');
      toast.success('Kampány elindítva');
      setCampaignToSend(null);
    } catch (error) {
      toast.error('Hiba a kampány indításakor');
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-4 w-full min-w-0">
      <CommonPageHeader
        title={tr.campaign.title}
        subtitle={tr.campaign.subtitle}
        icon={EnvelopeIcon}
      >
        <button
          onClick={() => navigate('/campaigns/new')}
          className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          <span>{tr.campaign.new}</span>
        </button>
      </CommonPageHeader>

      {/* Campaign List */}
      {campaigns.length === 0 ? (
        <div className="text-center py-16">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200 }}
            className="w-20 h-20 mx-auto bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-4 shadow-lg"
          >
            <EnvelopeIcon className="w-10 h-10 text-white" />
          </motion.div>
          <p className="text-gray-500 dark:text-gray-400 text-lg">{tr.campaign.noCampaigns}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <AnimatePresence>
            {campaigns.map((campaign, index) => (
              <motion.div
                key={campaign.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
              >
                {/* Card Header */}
                <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      {campaign.type === 'email' ? (
                        <EnvelopeIcon className="w-5 h-5 text-blue-500" />
                      ) : (
                        <ChatBubbleLeftRightIcon className="w-5 h-5 text-purple-500" />
                      )}
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                        {getTypeLabel(campaign.type)}
                      </span>
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${statusColors[campaign.status]}`}>
                      {getStatusLabel(campaign.status)}
                    </span>
                  </div>
                  <h3
                    className="font-bold text-gray-900 dark:text-white cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    onClick={() => navigate(`/campaigns/${campaign.id}`)}
                  >
                    {campaign.name}
                  </h3>
                  {campaign.emailContent?.subject && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">
                      {campaign.emailContent.subject}
                    </p>
                  )}
                </div>

                {/* Card Stats */}
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/50">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">
                        {campaign.stats.totalRecipients}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{tr.campaign.recipients}</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-green-600 dark:text-green-400">
                        {campaign.stats.delivered}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{tr.campaign.statsDelivered}</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                        {campaign.stats.opened}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{tr.campaign.statsOpened}</p>
                    </div>
                  </div>
                </div>

                {/* Card Footer */}
                <div className="px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center space-x-1 text-xs text-gray-400">
                    {campaign.status === 'sent' && campaign.sentAt ? (
                      <>
                        <CheckCircleIcon className="w-3.5 h-3.5" />
                        {formatDate(campaign.sentAt)}
                      </>
                    ) : campaign.status === 'scheduled' && campaign.schedule.scheduledAt ? (
                      <>
                        <ClockIcon className="w-3.5 h-3.5" />
                        {formatDate(campaign.schedule.scheduledAt)}
                      </>
                    ) : (
                      <>
                        <ClockIcon className="w-3.5 h-3.5" />
                        {formatDate(campaign.createdAt)}
                      </>
                    )}
                  </div>
                  <div className="flex items-center space-x-1">
                    {campaign.status === 'draft' && (
                      <button
                        onClick={() => setCampaignToSend(campaign)}
                        className="p-1.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                        title={tr.campaign.send}
                      >
                        <PaperAirplaneIcon className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => navigate(`/campaigns/${campaign.id}/stats`)}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      title={tr.campaign.statsTitle}
                    >
                      <ChartBarIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => navigate(`/campaigns/${campaign.id}/edit`)}
                      className="p-1.5 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title={tr.campaign.edit}
                    >
                      <PencilSquareIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => duplicateCampaign(campaign.id)}
                      className="p-1.5 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title={tr.campaign.duplicate}
                    >
                      <DocumentDuplicateIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setCampaignToDelete(campaign)}
                      className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title={tr.campaign.delete}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmDeleteModal
        isOpen={!!campaignToDelete}
        onClose={() => setCampaignToDelete(null)}
        onConfirm={() => {
          if (campaignToDelete) {
            deleteCampaign(campaignToDelete.id);
            setCampaignToDelete(null);
          }
        }}
        title={tr.campaign.delete}
        deleteMessage={tr.campaign.deleteConfirm}
        deleteWarning={tr.campaign.deleteWarning}
      />

      {/* Send Confirmation */}
      <ConfirmDeleteModal
        isOpen={!!campaignToSend}
        onClose={() => setCampaignToSend(null)}
        onConfirm={handleSend}
        title={tr.campaign.send}
        deleteMessage={tr.campaign.sendConfirm}
        deleteWarning={tr.campaign.sendWarning}
      />
    </div>
  );
};

export default Campaigns;
